import { isPlatformBrowser } from '@angular/common';
import { ApplicationRef, DOCUMENT, DestroyRef, PLATFORM_ID, Service, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { VersionReadyEvent } from '@angular/service-worker';
import { SwUpdate } from '@angular/service-worker';
import { concat, first, interval } from 'rxjs';
import { filter } from 'rxjs/operators';

import { AlertService } from '@app/core/messages/alert.service';

const CHECK_INTERVAL_MS = 6 * 60 * 60 * 1000;
const NEW_VERSION_MESSAGE = 'Uma nova versão do LeadGen está disponível.';
const NEW_VERSION_ACTION_LABEL = 'Atualizar';
const UPDATE_FAILED_MESSAGE = 'Não foi possível atualizar o LeadGen.';

@Service()
export class AppUpdateService {
  private readonly updates = inject(SwUpdate);
  private readonly appRef = inject(ApplicationRef);
  private readonly alerts = inject(AlertService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);

  init(): void {
    if (!this.updates.isEnabled) {
      return;
    }

    const whenStable = this.appRef.isStable.pipe(first(stable => stable === true));
    concat(whenStable, interval(CHECK_INTERVAL_MS))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => void this.updates.checkForUpdate());

    this.updates.versionUpdates
      .pipe(
        filter((e): e is VersionReadyEvent => e.type === 'VERSION_READY'),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.offerUpdate());
  }

  private offerUpdate(): void {
    this.alerts.withAction(NEW_VERSION_MESSAGE, NEW_VERSION_ACTION_LABEL, () => this.activateAndReload());
  }

  private activateAndReload(): void {
    this.updates.activateUpdate().then(
      () => this.reloadInBrowser(),
      () => this.alerts.errorWithRetry(UPDATE_FAILED_MESSAGE, () => this.activateAndReload()),
    );
  }

  private reloadInBrowser(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.document.location.reload();
    }
  }
}
